import { useCallback, useEffect, useState } from "react";
import { fetchPushConfig, savePushSubscription, removePushSubscription, sendPushTest } from "@lawyer/services/lawyerApi";

const SCOPE = "/lawyer-portal/";

function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((ch) => ch.charCodeAt(0)));
}

async function getRegistration() {
  if (!("serviceWorker" in navigator)) return null;
  return (await navigator.serviceWorker.getRegistration(SCOPE)) || (await navigator.serviceWorker.ready);
}

// Browser push for booking alerts: "unsupported" | "blocked" | "off" | "on" | "loading" | "unavailable"
export function usePushAlerts({ enabled = true } = {}) {
  const [state, setState] = useState("loading");
  const [busy, setBusy] = useState(false);
  const [publicKey, setPublicKey] = useState(null);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) { setState("unsupported"); return; }
    try {
      const cfg = await fetchPushConfig();
      if (!cfg.enabled || !cfg.public_key) { setState("unavailable"); return; }
      setPublicKey(cfg.public_key);
      if (Notification.permission === "denied") { setState("blocked"); return; }
      const reg = await getRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      setState(sub && Notification.permission === "granted" ? "on" : "off");
    } catch { setState("unavailable"); }
  }, [enabled]);

  useEffect(() => { refresh(); }, [refresh]);

  const enable = useCallback(async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setState(perm === "denied" ? "blocked" : "off"); return false; }
      const reg = await getRegistration();
      if (!reg) { setState("unavailable"); return false; }
      const sub = (await reg.pushManager.getSubscription())
        || (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) }));
      await savePushSubscription(sub.toJSON());
      setState("on");
      sendPushTest().catch(() => {});
      return true;
    } catch { setState("off"); return false; }
    finally { setBusy(false); }
  }, [publicKey]);

  const disable = useCallback(async () => {
    setBusy(true);
    try {
      const reg = await getRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) { await removePushSubscription(sub.endpoint).catch(() => {}); await sub.unsubscribe(); }
      setState("off");
    } finally { setBusy(false); }
  }, []);

  return { state, busy, enable, disable };
}
